/**
 * The monthly statement, calculated once.
 *
 * The statement page carried this arithmetic inline, with a comment explaining why
 * it added no server route: "inventing a second server-side path to the same
 * numbers is how a statement and a balance start disagreeing with each other."
 * That reasoning is right and the PDF does not get to ignore it — a printed
 * statement that disagrees with the screen it was printed from is worse than no
 * PDF at all. So the arithmetic moved here, and the page and the PDF both call it.
 *
 * It takes plain data, not a database handle, so it runs unchanged in the browser
 * and on the server.
 */

export const INFLOW_KINDS = ["rollover", "allocation", "topup", "refund", "adjustment"] as const;

/**
 * The English words, kept HERE because the PDF renders server-side and has no
 * Arabic font yet — it is English by decision, not by oversight.
 *
 * The browser does not read this map; it uses `inflowLabelKey` below and its own
 * dictionaries. The two cannot drift: src/i18n.test.ts asserts that every English
 * value here is identical to the one in src/i18n/en.ts.
 */
export const INFLOW_LABELS: Record<string, string> = {
  rollover: "Carried forward from last month",
  allocation: "Monthly contracted allocation",
  topup: "Additional credits purchased",
  refund: "Credits refunded",
  adjustment: "Manual adjustment",
};

/** The same row as a translation key, for anything with a reader who chose a
 *  language. `label` on the built statement stays English for the PDF. */
export function inflowLabelKey(kind: string): string {
  return `statement.inflow.${kind}`;
}

export type StatementEntry = { kind: string; credits: number };

export type StatementInput = {
  entries: StatementEntry[];
  balance: { reserved: number; available: number };
};

export type Statement = {
  inflows: Array<{ kind: string; label: string; credits: number }>;
  creditsIn: number;
  spent: number;
  committed: number;
  closing: number;
  /** False means the figures below disagree with the server's own balance. Show the
   *  disagreement; never print a confident wrong total. */
  reconciled: boolean;
  drift: number;
};

export function buildStatement(data: StatementInput): Statement {
  const sumOf = (kind: string) =>
    data.entries.filter((entry) => entry.kind === kind).reduce((total, entry) => total + entry.credits, 0);

  const inflows = INFLOW_KINDS.map((kind) => ({ kind, label: INFLOW_LABELS[kind], credits: sumOf(kind) }));
  const creditsIn = inflows.reduce((total, row) => total + row.credits, 0);
  const spent = Math.abs(sumOf("spend"));
  const committed = data.balance.reserved;
  const closing = creditsIn - spent - committed;

  // `closing` is rebuilt from the individual entry kinds; `balance.available` comes
  // from the server's own calculation over the same rows. They must agree. If an
  // entry kind is ever added and not listed in INFLOW_KINDS, this would quietly
  // under-report the client's money — so surface the disagreement instead.
  // The tolerance is a rounding epsilon, not a fudge factor.
  const drift = Math.abs(closing - data.balance.available);
  return { inflows, creditsIn, spent, committed, closing, reconciled: drift < 0.011, drift };
}

/**
 * Presentation rules that BOTH renderers must follow, for the same reason the
 * arithmetic is shared: the PDF is the statement, not a summary of it. When the
 * PDF rounded -0 away but the screen did not, the two documents stopped being the
 * same document.
 *
 * Note on zero rows: the PDF originally hid them as noise. The screen deliberately
 * keeps them, and its reasoning is on the row itself — "Additional credits
 * purchased: 0" answers a question, while a missing line leaves the reader unsure
 * whether it was zero or simply left out. The PDF follows the screen.
 */

/** A deduction of nothing reads "0", never "-0". A minus in front of a zero on a
 *  client statement looks like a defect, and looking like one costs the same trust
 *  as being one. */
export function deducted(value: number, format: (value: number) => string) {
  return value === 0 ? format(0) : `-${format(value)}`;
}

